// =============================================================================
// Shared UI components — BT-branded, accessible, consistent.
// =============================================================================
import type { ReactNode } from 'react'
import type { Brag } from '../lib/types'

// ---- Button
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md'
}
export function Button({ variant = 'secondary', size = 'md', className = '', children, ...props }: ButtonProps) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-all disabled:opacity-50 disabled:pointer-events-none'
  const sizes = { sm: 'px-2.5 py-1 text-[12px]', md: 'px-3.5 py-2 text-[13px]' }
  const variants = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm active:scale-[0.98]',
    secondary: 'border border-line bg-white text-ink hover:bg-canvas hover:border-brand-200',
    ghost: 'text-muted hover:text-ink hover:bg-canvas',
  }
  return <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...props}>{children}</button>
}

// ---- Card
export function Card({ className = '', children, style }: { className?: string; children: ReactNode; style?: React.CSSProperties }) {
  return <div className={`rounded-card border border-line bg-white shadow-card ${className}`} style={style}>{children}</div>
}

// ---- RAG chip (strong colours, visible against white)
const RAG_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Red: { bg: 'bg-red-50', text: 'text-[#d4380d]', border: 'border-red-200' },
  Amber: { bg: 'bg-orange-50', text: 'text-[#d48806]', border: 'border-orange-200' },
  Green: { bg: 'bg-emerald-50', text: 'text-[#389e0d]', border: 'border-emerald-200' },
  Blue: { bg: 'bg-blue-50', text: 'text-[#0958d9]', border: 'border-blue-200' },
}
export function RagChip({ rag, size = 'md', label }: { rag: Brag | string | null; size?: 'sm' | 'md'; label?: string }) {
  const style = RAG_COLORS[rag || ''] || { bg: 'bg-slate-50', text: 'text-muted', border: 'border-slate-200' }
  const sz = size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
  return <span className={`inline-flex items-center rounded-full border font-semibold ${style.bg} ${style.text} ${style.border} ${sz}`}>{label || rag || '—'}</span>
}

// ---- RAG dot (small coloured circle)
const RAG_DOT_COLORS: Record<string, string> = { Red: 'bg-[#d4380d]', Amber: 'bg-[#d48806]', Green: 'bg-[#389e0d]', Blue: 'bg-[#0958d9]' }
export function RagDot({ rag }: { rag: string | null }) {
  const cls = RAG_DOT_COLORS[rag || ''] || 'bg-slate-300'
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${cls}`} title={rag || '—'} />
}

// ---- Pill
const PILL_TONES: Record<string, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  brand: 'bg-brand-50 text-brand-700',
  success: 'bg-emerald-50 text-emerald-700',
  warn: 'bg-orange-50 text-orange-700',
}
export function Pill({ tone = 'neutral', children }: { tone?: string; children: ReactNode }) {
  const cls = PILL_TONES[tone] || PILL_TONES.neutral
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{children}</span>
}

// ---- Section title
export function SectionTitle({ children, sub, right }: { children: ReactNode; sub?: string; right?: ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-ink">{children}</h1>
        {sub && <p className="mt-0.5 text-[12px] text-muted">{sub}</p>}
      </div>
      {right}
    </div>
  )
}

// ---- States
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-slate-100 ${className}`} />
}
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-sm text-rag-red">{message}</p>
      {onRetry && <Button size="sm" onClick={onRetry}>Retry</Button>}
    </div>
  )
}
export function EmptyState({ title, hint, icon }: { title: string; hint?: string; icon?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      {icon && <span className="text-3xl">{icon}</span>}
      <p className="text-sm font-medium text-ink">{title}</p>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  )
}
